/**
 * 416 the commander's rally (DECISIONS 416, a legible skill — the survey's E, §5.52 §6; behind `GameConfig.commanderRally`,
 * off unless true, one value or per team). A squad cut to half or less in a fight falls back to a rally point behind it
 * instead of dying where it stands: on the way from where it is to the side's nearest point it owns farther from the
 * contact (else its base), 30–50 people back, at a spot where a crouched body is hidden from the contact by the ground or
 * the props (the ambush's spot rule, ./spots.ts) — else 40 people back in the open. There it holds (the hold's cover posts, as built) till it is whole again
 * — its respawned members walked in to it (68.10 puts them back in the squad) — or for `rallySeconds`, and the commander
 * has it again, the squad together.
 *
 * Legible: the squad's task carries `skill: { name: 'rally', phase, since }` — `falling back` (to the point) → `rallying`
 * (held there; the card's count is its people on foot).
 *
 * Two hooks in the commander (ai/squadOrders.ts), as the ambush's: `step` once a second picks the squads and moves the
 * phases; `holds` keeps a rallying squad's own order through the re-plan. The task is the routine's rank (6, claim `rally`),
 * so a play, an objective, an errand or a human takes the squad back. The bounds back (C's withdraw call) stay the lead
 * step's: the squad walks to the hold as any hold. Lane C's leaderless remnant (415 #3(b), `drillCallEarly`) withdraws by
 * its own brain; the ledger's `leaderless` says when both could fire on the same squad. Pure in the state, with the
 * planner's own memory.
 */
import type { GameState, SquadOrder, Stance, Vec3 } from '../../types';
import type { SkillTag, TaskClaim } from '../../types';
import { PHYS_DT } from '../../constants';
import { personSetOf, PERSON_BODY_HEIGHT } from '../../core/scaleProfile';
import { teamPicture } from '../../core/picture';
import { personPointOn } from '../../core/fineTerrain';
import { brainFace } from '../../core/brainFace';
import { squadOf } from '../../core/squads';
import { vecNormalize } from '../../math';
import { arc, besides, hiddenSpot, slerp, takenBy } from './spots';

export const RALLY_PLAN = {
  /** Squads of this many or more are rallied (a pair down to one is spent, not rallied). */
  minSize: 3,
  /**
   * Cut to half or less: its fighting group — on foot within `withPeople` of its member nearest the enemy — at most this
   * share of the squad (rounded down). Not its people on foot anywhere: a respawn wave puts the dead back in the squad at
   * the base at once (fhTug25, fh-1/2/3/5, counted so: 42 of 59 rallies ended 'whole' within 1–5 s, still falling back).
   */
  halfShare: 0.5,
  withPeople: 30,
  /**
   * In a fight: a member killed within `lossSeconds` (read from its members alive: a member boarding is no loss). The fire
   * it hides from: `threatPeople` out from its people toward the side's nearest belief (any range), else toward the enemy's
   * base. (The measure that set this trigger — "a squad cut to one knew no enemy 20 times in 21" — counted boarding as a
   * loss and is retracted: at cuts by deaths the side's picture was never empty, sweeps/2026-11-02-fire-tells-team.md.)
   */
  lossSeconds: 15,
  threatPeople: 40,
  /**
   * The rally point: tried this far back from the squad toward the side's anchor, nearest first (people); the anchor itself
   * when nearer. Where nothing hides (at Foothold's start, 22 of 27 places on the line between the bases), the plain point
   * `plainPeople` back: out of the contact is the rally; hidden is the better rally.
   */
  backPeople: [30, 40, 50],
  plainPeople: 40,
  /** Hidden at the rally point (./spots.ts): the stance, how near the way's point, the room round it (people). */
  hideStance: 'crouch' as Stance,
  spotPeople: 10,
  roomPeople: 6,
  roomSpots: 2,
  /** The eyes it hides from: the contact, and as far to either side of it (people). */
  eyeSidePeople: 10,
  /** At the rally point: the leader's hold within this of it (people). */
  arrivePeople: 5,
  /** Whole again: as many on foot within this of the rally point as the squad has (people) — the respawned have walked in. */
  joinPeople: 10,
  /** Rallying this long at most (s), then the commander has it again. */
  rallySeconds: 60,
  /** After a rally ends, none for this squad for this long (s). */
  cooldownSeconds: 60,
  /** A task runs this long unless renewed (s); `step` renews it every `renewSeconds`. */
  taskSeconds: 15,
  renewSeconds: 10,
};

/** Is the commander's rally on for `team` (one value or its side's; off unless true)? */
export function rallyOn(state: Pick<GameState, 'config'>, team: number): boolean {
  const v = state.config.commanderRally;
  return Array.isArray(v) ? v[team] === true : v === true;
}

/**
 * Where a squad of `team` at `from` in a fight with `contact` (both on planet `planetIndex`) rallies: on the way from it to
 * the nearest point its side owns farther from the contact than it is (else the side's base), each of `backPeople` back (the
 * anchor itself when nearer), the hidden spot nearest the way's point from the contact and either side of it; where none
 * hides, the way's point `plainPeople` back (`hidden` false). Null when the side has no anchor there. Pure.
 */
export function rallyPoint(state: GameState, team: number, planetIndex: number, from: Vec3, contact: Vec3): { dir: Vec3; people: number; hidden: boolean } | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, r = planet.radius;
  const behind = arc(from, contact);
  let anchor: Vec3 | null = null, best = Infinity;
  for (const s of state.modeState.points?.sites ?? []) {
    if (s.owner !== team || s.planetIndex !== planetIndex || arc(s.surfaceDir, contact) <= behind) continue;
    const d = arc(s.surfaceDir, from);
    if (d < best) { best = d; anchor = s.surfaceDir; }
  }
  anchor ??= state.board?.bases?.find(b => b.team === team && b.planetIndex === planetIndex)?.dir ?? null;
  if (!anchor) return null;
  const way = arc(from, anchor);
  const eyes = [contact, ...besides(contact, from, (RALLY_PLAN.eyeSidePeople * person) / r)].map(d => personPointOn(planet, vecNormalize(d), ps, ps.losEyeHeight));
  for (const people of RALLY_PLAN.backPeople) {
    const back = Math.min((people * person) / r, way);
    const here = way < 1e-9 ? vecNormalize(anchor) : slerp(from, anchor, back / way);
    const pick = hiddenSpot(state, planetIndex, here, eyes, RALLY_PLAN);
    if (pick) return { dir: pick, people: Math.round((back * r) / person), hidden: true };
    if (back >= way) break;
  }
  const back = Math.min((RALLY_PLAN.plainPeople * person) / r, way);
  return { dir: way < 1e-9 ? vecNormalize(anchor) : slerp(from, anchor, back / way), people: Math.round((back * r) / person), hidden: false };
}

type Phase = 'falling back' | 'rallying';
/**
 * One rally: the squad, when it was ordered and ended, its last phase and why it ended (and what took it, when claimed);
 * its size, its fighting group at the order and its people at the rally point at the end; and whether it was leaderless at the order (its designated
 * leader down: 415 #3(b)'s remnant, which withdraws by its own brain — the overlap, measured).
 */
export interface RallyRecord { squadId: number; picked: number; ended: number; last: Phase; why: 'whole' | 'time' | 'wiped' | 'mounted' | 'claimed' | 'gone'; by?: string; leaderless: boolean; size: number; onFootAtOrder: number; onFootAtEnd: number; hidden: boolean; known: boolean }
interface Rally { planetIndex: number; site: Vec3; phase: Phase; since: number; picked: number; issued: number; heldFrom?: number; size: number; onFoot: number; leaderless: boolean; hidden: boolean; known: boolean }
interface PlanSquad { id: number; unitIds: number[] }
/** The commander's issue as this planner uses it (ai/squadOrders.ts IssueOrder, the claim with its skill). */
export type RallyIssue<Sq extends PlanSquad> = (squad: Sq, order: SquadOrder, tier: 'base' | 'task', expires?: number, units?: number[], claim?: { claim?: TaskClaim; skill?: SkillTag }) => unknown;

/** The planner: one a side, its memory the squads rallying (and the ones cooling down). */
export function createRallyPlanner(team: number) {
  const rallying = new Map<number, Rally>();
  const cooling = new Map<number, number>();   // squad id → the tick it may rally again
  // squad id → its members alive at the last look, the tick it last lost one (a death: read from the living, not from its
  // people on foot — a member climbing into a vehicle is no loss, and counted so it faked cuts, fix/416-rally-board)
  const aliveWas = new Map<number, number>(), lostAt = new Map<number, number>();
  const second = Math.max(1, Math.round(1 / PHYS_DT));
  const ledger: RallyRecord[] = [];

  const living = (st: GameState, sq: PlanSquad) => sq.unitIds.map(id => st.players.find(p => p.id === id)).filter(u => !!u && u.alive);
  const onFoot = (st: GameState, sq: PlanSquad) => living(st, sq).filter(u => !u!.mount && !u!.airborne);
  const give = <Sq extends PlanSquad>(issue: RallyIssue<Sq>, sq: Sq, l: Rally, tick: number) => {
    l.issued = tick;
    return issue(sq, { kind: 'hold', planetIndex: l.planetIndex, surfaceDir: l.site }, 'task', tick + RALLY_PLAN.taskSeconds * second, undefined,
      { claim: 'rally', skill: { name: 'rally', phase: l.phase, since: l.since } });
  };
  const end = (id: number, tick: number, why: RallyRecord['why'], onFootNow: number, by?: string) => {
    const l = rallying.get(id);
    if (l) ledger.push({ squadId: id, picked: l.picked, ended: tick, last: l.phase, why, ...(by ? { by } : {}), leaderless: l.leaderless, size: l.size, onFootAtOrder: l.onFoot, onFootAtEnd: onFootNow, hidden: l.hidden, known: l.known });
    rallying.delete(id); cooling.set(id, tick + RALLY_PLAN.cooldownSeconds * second);
  };

  /**
   * Each squad of the side not rallying or cooling down, `minSize` or more, its fighting group cut to half or less with one
   * lost in the last `lossSeconds`, free for the routine, carrying no other skill: a rally point read for it against the
   * fire it expects (`threatPeople` toward the side's nearest belief, else the enemy's base), and the task given.
   */
  const pick = <Sq extends PlanSquad>(st: GameState, squads: readonly Sq[], tick: number, free: (sq: Sq) => boolean, issue: RallyIssue<Sq>): void => {
    const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
    let pic: ReturnType<typeof teamPicture> | null = null;
    for (const sq of squads) {
      const alive = living(st, sq).length;
      if (alive < (aliveWas.get(sq.id) ?? 0)) lostAt.set(sq.id, tick);
      aliveWas.set(sq.id, alive);
      const us = onFoot(st, sq);
      if (rallying.has(sq.id) || (cooling.get(sq.id) ?? -1) > tick) continue;
      const size = sq.unitIds.length;
      if (size < RALLY_PLAN.minSize) continue;
      if (us.length === 0 || !free(sq)) continue;
      if (tick - (lostAt.get(sq.id) ?? -Infinity) > RALLY_PLAN.lossSeconds * second) continue;
      const ms = squadOf(st, sq.unitIds[0]);
      if (ms?.tasks.some(t => t.skill && !(t.expires !== undefined && tick >= t.expires))) continue;   // (another skill has it: an ambush)
      pic ??= teamPicture(st, team, tick);
      const pi = us[0]!.planetIndex, r = st.planets[pi].radius;
      let toward: Vec3 | null = null, near = Infinity;
      for (const b of pic) {
        if (b.planetIndex !== pi) continue;
        for (const u of us) { const d = arc(b.surfaceDir, u!.surfaceDir); if (d < near) { near = d; toward = b.surfaceDir; } }
      }
      const known = toward !== null;
      toward ??= st.board?.bases?.find(b => b.team !== team && b.planetIndex === pi)?.dir ?? null;
      if (!toward) continue;
      // the fighting group: on foot within withPeople of its member nearest the enemy (the respawned at the base are not in it)
      let front = us[0]!, fd = Infinity;
      for (const u of us) { const d = arc(u!.surfaceDir, toward); if (d < fd) { fd = d; front = u!; } }
      const group = us.filter(u => u!.planetIndex === pi && arc(u!.surfaceDir, front.surfaceDir) * r <= RALLY_PLAN.withPeople * person).length;
      if (group > Math.floor(size * RALLY_PLAN.halfShare)) continue;
      const from = front.surfaceDir;
      if (arc(from, toward) < 1e-9) continue;
      const contact = slerp(from, toward, Math.min(1, (RALLY_PLAN.threatPeople * person) / r / arc(from, toward)));
      const at = rallyPoint(st, team, pi, from, contact);
      if (!at) continue;
      const leader = ms?.leader;
      const leaderless = leader !== undefined && !st.players.find(p => p.id === leader)?.alive;
      const l: Rally = { planetIndex: pi, site: at.dir, phase: 'falling back', since: tick, picked: tick, issued: tick, size, onFoot: group, leaderless, hidden: at.hidden, known };
      rallying.set(sq.id, l);
      give(issue, sq, l, tick);
    }
  };

  return {
    /** Which squads rally now (squad id → its phase), for the harness and the tests. */
    phases: (): Map<number, Phase> => new Map([...rallying].map(([id, l]) => [id, l.phase])),
    records: (): readonly RallyRecord[] => ledger,
    /** Is squad `id` rallying (any phase)? The commander keeps its own order meanwhile — it fell back from it, and the re-plan would otherwise hand it on. */
    holds: (id: number): boolean => rallying.has(id),

    /**
     * Once a second: each rallying squad — its phase from its leader's step (held at the point), the task given again when
     * the phase moves or it is due; let go once whole at the point, after `rallySeconds` there, wiped, or taken (`free`
     * false). Then the pick (`plan`'s), each second: a loss is answered within the second, not at the next re-plan.
     */
    /** The re-plan's row (COMMANDER_PLANNERS, after the ambush's): the pick, before the planners after it hand the squads on. */
    plan<Sq extends PlanSquad>(st: GameState, squads: readonly Sq[], tick: number, free: (sq: Sq) => boolean, issue: RallyIssue<Sq>): void { pick(st, squads, tick, free, issue); },

    step<Sq extends PlanSquad>(st: GameState, squads: readonly Sq[], tick: number, free: (sq: Sq) => boolean, issue: RallyIssue<Sq>): void {
      if (tick % second !== 0) return;
      const person = PERSON_BODY_HEIGHT * personSetOf(st).k;
      for (const [id, l] of [...rallying]) {
        const sq = squads.find(s => s.id === id);
        const us = sq ? onFoot(st, sq) : [];
        if (!sq || us.length === 0 || !free(sq)) { end(id, tick, !sq ? 'gone' : us.length === 0 ? (living(st, sq).length ? 'mounted' : 'wiped') : 'claimed', us.length, sq && us.length ? takenBy(st, sq.unitIds, tick) : undefined); continue; }
        const r = st.planets[l.planetIndex].radius;
        const lead = sq.unitIds.map(u => brainFace(st, u)?.lead).find(x => x?.kind === 'hold' && x.planetIndex === l.planetIndex && arc(x.to, l.site) * r <= RALLY_PLAN.arrivePeople * person);
        const there = us.filter(u => arc(u!.surfaceDir, l.site) * r <= RALLY_PLAN.joinPeople * person).length;
        if (there >= l.size) { end(id, tick, 'whole', there); continue; }
        let phase: Phase = l.phase;
        if (phase === 'falling back' && lead) phase = 'rallying';
        if (phase === 'rallying') {
          l.heldFrom ??= tick;
          if (tick - l.heldFrom >= RALLY_PLAN.rallySeconds * second) { end(id, tick, 'time', there); continue; }
        }
        if (phase !== l.phase) { l.phase = phase; l.since = tick; give(issue, sq, l, tick); }
        else if (tick - l.issued >= RALLY_PLAN.renewSeconds * second) give(issue, sq, l, tick);
      }
      pick(st, squads, tick, free, issue);
    },
  };
}
